import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  AddRemediationSolutionDTO,
  IntegrationSubmitPayload,
  ToolsServicesOverviewResponse,
  ViewRemediationSolutionDTO,
  ConnectMetadataResponse,
  UserConnectStartResponse,
  UserConnectCallbackResponse,
  ConnectionStatusResponse,
} from '../interfaces';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ToolsAndServicesService {
  private baseUrl = environment.baseUrl;
  constructor(private http: HttpClient) {}

  public fetchToolsAndServicesOverview(): Observable<ToolsServicesOverviewResponse> {
    return this.http.get<ToolsServicesOverviewResponse>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/overview`,
    );
  }

  public fetchVendors(
    solutionType: 'Tool' | 'Service',
  ): Observable<{ id: string; name: string }[]> {
    return this.http
      .get<{
        items: { vendorId: string; vendorName: string }[];
      }>(`${this.baseUrl}/CMDISOCustomerRemediationSolution/vendors`, {
        params: new HttpParams().set('solutionType', solutionType),
      })
      .pipe(
        map((options) => {
          return [
            ...options.items.map((item) => ({
              id: item.vendorId,
              name: item.vendorName,
            })),
            { id: 'other', name: 'Other' },
          ];
        }),
      );
  }

  public fetchProductOptions(
    vendorId: string,
    solutionType: 'Tool' | 'Service',
  ): Observable<
    { id: string; name: string; exists: boolean; currentlyInStack: boolean }[]
  > {
    return this.http
      .get<{
        items: {
          solutionId: string;
          displayName: string;
          exists: boolean;
          currentlyInStack: boolean;
        }[];
      }>(`${this.baseUrl}/CMDISOCustomerRemediationSolution/by-vendor`, {
        params: new HttpParams()
          .set('solutionType', solutionType)
          .set('vendorId', vendorId),
      })
      .pipe(
        map((options) => {
          return [
            ...options.items.map((item) => ({
              id: item.solutionId,
              name: item.displayName,
              exists: item.exists,
              currentlyInStack: item.currentlyInStack,
            })),
            {
              id: 'other',
              name: 'Other',
              exists: false,
              currentlyInStack: false,
            },
          ];
        }),
      );
  }

  public fetchLicenseOptions(solutionId: string): Observable<{
    licenseOptions: {
      id: string;
      name: string;
      fitsSecurityPlan?: boolean;
    }[];
    planWindow: {
      activePlanEndUtc?: string;
      approvedEndUtc?: string;
      approvedStartUtc?: string;
      hasActivePlan: boolean;
      hasApprovedPlan: boolean;
    };
  }> {
    return this.http
      .get<{
        planWindow: {
          activePlanEndUtc?: string;
          approvedEndUtc?: string;
          approvedStartUtc?: string;
          hasActivePlan: boolean;
          hasApprovedPlan: boolean;
        };
        items: {
          licenseTypeId: string;
          displayName: string;
          fitsSecurityPlan: boolean;
        }[];
      }>(`${this.baseUrl}/CMDISOCustomerRemediationSolution/GetLicenses`, {
        params: new HttpParams().set('solutionId', solutionId),
      })
      .pipe(
        map((options) => {
          return {
            licenseOptions: [
              ...options.items.map((item) => ({
                id: item.licenseTypeId,
                name: item.displayName,
                fitsSecurityPlan: item.fitsSecurityPlan,
              })),
              {
                id: 'other',
                name: 'Other',
              },
            ],
            planWindow: options.planWindow,
          };
        }),
      );
  }

  public checkRemoveSubSidebar(solutionId: string): Observable<{
    blocked: boolean;
    message: {
      messageType: string;
      titleId: string;
      contentId: string;
      integrationRemovalId?: string;
    };
    customerSolutionId: string;
  }> {
    // return of({ isBlocked: true } );

    const params = new HttpParams().set('customerSolutionId', solutionId);
    return this.http.get<{
      blocked: boolean;
      message: {
        messageType: string;
        titleId: string;
        contentId: string;
        integrationRemovalId?: string;
      };
      customerSolutionId: string;
    }>(`${this.baseUrl}/CMDISOCustomerRemediationSolution/remove-check`, {
      params,
    });
  }

  public removeSolution(customerSolutionId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/remove`,
      {
        params: new HttpParams().set('customerSolutionId', customerSolutionId),
      },
    );
  }

  public fetchCategoryOptions(): Observable<{
    categories: { categoryId: string; categoryName: string; features: any[] }[];
  }> {
    return this.http.get<{
      categories: {
        categoryId: string;
        categoryName: string;
        features: any[];
      }[];
    }>(
      `${this.baseUrl}/CMDISORemediationSolutionsCategoriesTemplate/security-catalog`,
    );
  }

  public addEditRemediationSolution(
    remediationSolution: AddRemediationSolutionDTO,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/save`,
      remediationSolution,
    );
  }

  public fetchRemediationSolutionDetails(
    solutionId: string,
  ): Observable<ViewRemediationSolutionDTO> {
    return this.http.get<ViewRemediationSolutionDTO>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/${solutionId}/view`,
    );
  }

  public fetchConnectMetadata(
    solutionId: string,
  ): Observable<ConnectMetadataResponse> {
    return this.http.get<ConnectMetadataResponse>(
      `${this.baseUrl}/CMDISOIntegrations/${solutionId}/connect-metadata`,
    );
  }

  public startUserConnectFlow(
    solutionId: string,
    integrationServiceId: string,
    values: Record<string, string | boolean | string[]>,
    redirectUri: string,
    selectedLicenseId: string,
  ): Observable<UserConnectStartResponse> {
    return this.http.post<UserConnectStartResponse>(
      `${this.baseUrl}/CMDISOIntegrations/${solutionId}/user-connect/start`,
      {
        integrationServiceId,
        values,
        redirectUri,
        selectedLicenseId,
      },
    );
  }

  public submitUserConnectCallback(
    state: string,
    providerCallback: Record<string, string>,
  ): Observable<UserConnectCallbackResponse> {
    return this.http.post<UserConnectCallbackResponse>(
      `${this.baseUrl}/CMDISOIntegrations/user-connect/callback`,
      {
        state,
        providerCallback,
      },
    );
  }

  public fetchConnectionStatus(
    toolId: string,
    integrationServiceId?: string,
  ): Observable<ConnectionStatusResponse> {
    let params = new HttpParams();
    if (integrationServiceId) {
      params = params.set('integrationServiceId', integrationServiceId);
    }
    return this.http.get<ConnectionStatusResponse>(
      `${this.baseUrl}/CMDISOIntegrations/${toolId}/connection-status`,
      { params },
    );
  }

  disconnectIntegration(
    solutionId: string,
    integrationServiceId: string,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/CMDISOIntegrations/${solutionId}/disconnect`,
      {
        integrationServiceId,
      },
    );
  }

  public submitIntegrationConnection(
    payload: IntegrationSubmitPayload,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/${payload.solutionId}/integration/connect`,
      payload,
    );
  }

  public removeIntegration(solutionId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/CMDISOCustomerRemediationSolution/${solutionId}/integration/remove`,
      {},
    );
  }
}
